import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTheme } from '@pushpay/theming';
import styles from './PageNavigation.module.css';

export interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

export interface KebabMenuItem {
  label: string;
  icon?: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  onClick: () => void;
  mobileOnly?: boolean;
}

export interface PrimaryAction {
  label: string;
  icon?: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  onClick: () => void;
  displayStyle?: 'primary' | 'secondary';
  showOnMobile?: boolean;
}

interface PageNavigationProps {
  showBreadcrumbs?: boolean;
  breadcrumbs?: Breadcrumb[];
  primaryAction?: PrimaryAction | null;
  showEdit?: boolean;
  onEdit?: () => void;
  showSettings?: boolean;
  onSettings?: () => void;
  kebabMenuItems?: KebabMenuItem[];
}

export const PageNavigation: React.FC<PageNavigationProps> = ({
  showBreadcrumbs = false,
  breadcrumbs = [],
  primaryAction = null,
  showEdit = false,
  onEdit,
  showSettings = false,
  onSettings,
  kebabMenuItems = [],
}) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const theme = useTheme();
  
  const primaryColor = theme.colors['product-color'] || '#006AFA';
  const hoverColor = theme.colors['product-color-dark-100'] || '#054AD1';

  const hasActions = showEdit || showSettings || kebabMenuItems.length > 0;

  return (
    <div className={styles.topRow}>
      {/* Breadcrumbs */}
      {showBreadcrumbs && breadcrumbs.length > 0 && (
        <div className={styles.breadcrumbs}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <LucideIcons.ChevronRight
                  size={16}
                  className={styles.breadcrumbSeparator}
                />
              )}
              {breadcrumb.onClick ? (
                <button
                  onClick={breadcrumb.onClick}
                  className={styles.breadcrumbLink}
                >
                  {breadcrumb.label}
                </button>
              ) : (
                <span className={styles.breadcrumbCurrent}>
                  {breadcrumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Actions */}
      {(primaryAction || hasActions) && (
        <div className={styles.headerActions}>
          {/* Primary Action Button */}
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className={`${
                primaryAction.displayStyle === 'secondary' ? styles.secondaryButton : styles.primaryButton
              } ${primaryAction.showOnMobile ? styles.showOnMobile : ''}`}
              style={
                primaryAction.displayStyle === 'secondary'
                  ? undefined
                  : {
                      backgroundColor: primaryColor,
                      // @ts-ignore - CSS custom property for hover state
                      '--hover-bg-color': hoverColor,
                    } as React.CSSProperties
              }
              aria-label={primaryAction.label}
            >
              {primaryAction.icon && (
                <primaryAction.icon size={18} />
              )}
              <span className={styles.buttonLabel}>
                {primaryAction.label}
              </span>
            </button>
          )}

          {/* Edit Button */}
          {showEdit && (
            <button
              onClick={onEdit}
              className={styles.iconButton}
              aria-label="Edit"
            >
              <LucideIcons.Pencil size={18} color="#1f2937" />
            </button>
          )}

          {/* Settings Button */}
          {showSettings && (
            <button
              onClick={onSettings}
              className={styles.iconButton}
              aria-label="Settings"
            >
              <LucideIcons.Settings size={18} color="#1f2937" />
            </button>
          )}

          {/* Kebab Menu */}
          {kebabMenuItems.length > 0 && (
            <div className={styles.actionsMenuContainer}>
              <button
                onClick={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                className={styles.iconButton}
                aria-label="More actions"
              >
                <LucideIcons.MoreVertical size={18} color="#1f2937" />
              </button>
              {isActionsMenuOpen && (
                <>
                  <div
                    onClick={() => setIsActionsMenuOpen(false)}
                    className={styles.actionsMenuBackdrop}
                  />
                  <div className={styles.actionsMenu}>
                    {kebabMenuItems.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            item.onClick();
                            setIsActionsMenuOpen(false);
                          }}
                          className={`${styles.actionsMenuItem} ${item.mobileOnly ? styles.mobileOnly : ''}`}
                        >
                          {IconComponent && (
                            <IconComponent
                              size={16}
                              style={{ marginRight: '8px' }}
                            />
                          )}
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
